import { useState, useMemo, useCallback } from "react";
import { ColumnDef } from "../types";

export interface UseTableSearchOptions<T> {
  columns: ColumnDef<T>[];
  controlledValue?: string;
  onSearchChange?: (value: string) => void;
}

export interface UseTableSearchResult<T> {
  searchValue: string;
  setSearchValue: (value: string) => void;
  filteredData: T[];
  isSearching: boolean;
}

export function useTableSearch<T>(
  data: T[],
  { columns, controlledValue, onSearchChange }: UseTableSearchOptions<T>,
): UseTableSearchResult<T> {
  // Internal state for uncontrolled mode
  const [internalValue, setInternalValue] = useState("");

  // Use controlled value if provided, otherwise use internal
  const searchValue = controlledValue !== undefined ? controlledValue : internalValue;

  const setSearchValue = useCallback(
    (value: string) => {
      if (controlledValue === undefined) {
        setInternalValue(value);
      }
      onSearchChange?.(value);
    },
    [controlledValue, onSearchChange],
  );

  // Determine which columns are searchable
  const searchableColumns = useMemo(() => {
    return columns.filter((col) => {
      // Explicit searchable setting takes precedence
      if (col.searchable !== undefined) return col.searchable;
      // Default: searchable if no custom render function
      return !col.render;
    });
  }, [columns]);

  // Memoized filtered data
  const filteredData = useMemo(() => {
    if (!searchValue.trim()) return data;

    const searchLower = searchValue.toLowerCase().trim();

    return data.filter((row) => {
      return searchableColumns.some((col) => {
        // Get the raw value
        const rawValue = col.accessor ? col.accessor(row) : (row as any)[col.key];

        // Get searchable text
        const searchText = col.searchValue ? col.searchValue(rawValue, row) : String(rawValue ?? "");

        return searchText.toLowerCase().includes(searchLower);
      });
    });
  }, [data, searchValue, searchableColumns]);

  return {
    searchValue,
    setSearchValue,
    filteredData,
    isSearching: searchValue.trim().length > 0,
  };
}
